import { ApiClient } from '@listening/api-contract/api-client-tag';
import type {
  FolderDetail,
  FolderSummary,
  PatchUpdateFolderRequest,
  PostCreateFolderRequest,
} from '@listening/api-contract/legacy-client.gen.ts';
import { SchemaUtils } from '@listening/api-contract/schema-utils';
import { Effect, type Schema } from 'effect';

// Row shape: FolderSummary (id, legacy_id, num_items, path, thumbnail_urls).
// FolderDetail (adds audio_conversion_ids + child_folder_ids) is per-id-fetch
// via getFolderEffect; the files/$folderId route loader prefetches it.
//
// `FolderSummary`/`FolderDetail` are Schema *classes*, but every cached value
// goes through `SchemaUtils.schemaInstanceToPlain` first, so the cache holds
// PLAIN objects. `Plain<T>` reflects that (homomorphic mapped type, no class
// prototype) — without it, spreading a cached row (`{ ...folder, num_items }`)
// trips `no-misused-spread`, which is a false positive on already-plain data.
type Plain<T> = { [K in keyof T]: T[K] };
export type FolderRow = Plain<Schema.Schema.Type<typeof FolderSummary>>;
export type FolderDetailRow = Plain<Schema.Schema.Type<typeof FolderDetail>>;

// —— Query keys ——

export const foldersRootQueryKey = ['folders'] as const;
export const foldersQueryKey = [...foldersRootQueryKey, 'list'] as const;
export const folderDetailQueryKey = (folderId: string) =>
  [...foldersRootQueryKey, 'detail', folderId] as const;

// Folder list + detail freshness (matches pre-migration). Folders change only
// via this client's own create/rename/move/delete mutations (which optimistically
// patch + invalidate), so a 5-min staleTime is plenty and avoids refetching on
// every mount / window focus.
export const FOLDERS_STALE_TIME_MS = 5 * 60 * 1000;

// —— Effect programs ——

export const listFoldersEffect = Effect.flatMap(ApiClient, (api) =>
  api.Folder.ListFolders().pipe(
    Effect.map((r) => r.folders.map(SchemaUtils.schemaInstanceToPlain)),
  ),
);

export const getFolderEffect = (folderId: string) =>
  Effect.flatMap(ApiClient, (api) =>
    api.Folder.GetFolder({ params: { folderId } }).pipe(
      Effect.map((r) => SchemaUtils.schemaInstanceToPlain(r.folder)),
    ),
  );

export type CreateFolderPayload = Plain<
  Schema.Schema.Type<typeof PostCreateFolderRequest>
>;

export const createFolderEffect = (payload: CreateFolderPayload) =>
  Effect.flatMap(ApiClient, (api) =>
    api.Folder.CreateFolder({ payload }).pipe(
      Effect.map((r) => SchemaUtils.schemaInstanceToPlain(r.folder)),
    ),
  );

export type UpdateFolderPayload = Plain<
  Schema.Schema.Type<typeof PatchUpdateFolderRequest>
>;

export const updateFolderEffect = (
  folderId: string,
  payload: Partial<UpdateFolderPayload>,
) =>
  Effect.flatMap(ApiClient, (api) =>
    api.Folder.UpdateFolder({
      params: { folderId },
      payload: {
        path: payload.path ?? null,
        audio_conversion_ids: payload.audio_conversion_ids ?? null,
      },
    }).pipe(Effect.map((r) => SchemaUtils.schemaInstanceToPlain(r.folder))),
  );

export const deleteFolderEffect = (folderId: string, recursive: boolean) =>
  Effect.flatMap(ApiClient, (api) =>
    api.Folder.DeleteFolder({
      params: { folderId },
      query: { recursive },
    }),
  );
